import createRegulatorPortalInstance from "../../api/RegulatorPortalInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import type { ApiSuccessEnvelope } from "../../types/affiliates";

/**
 * The whole regulator `/me` contract lives in this file — wire shapes included.
 * Everything downstream — the login fork, the route registration hook, the
 * guard, the holding screen — consumes `RegulatorMe`, so contract drift at
 * integration is a one-file fix.
 */

export type RegulatorStatus = "pending" | "approved";
export type ReportingPack = "gli" | "gli_brazil" | "gli_peru";

const REPORTING_PACKS: readonly ReportingPack[] = [
  "gli",
  "gli_brazil",
  "gli_peru",
];

/** Raw `audience` block the BE synthesizes for regulator callers. */
export interface RegulatorMeResponseData {
  audience?: string | null;
  status?: RegulatorStatus | string | null;
  email?: string | null;
  // Portal enablement for this client. BE has shipped both spellings across
  // envs; `parseRegulatorMe` reads either and exposes one boolean.
  regulator_portal_enabled?: boolean | null;
  portal_enabled?: boolean | null;
  allowed_reporting_packs?: unknown;
  default_reporting_pack?: unknown;
}

/** Normalized view of `/me` — the only regulator shape the app reads. */
export interface RegulatorMe {
  isRegulator: boolean;
  status: RegulatorStatus | null;
  portalEnabled: boolean;
  email: string | null;
  allowedReportingPacks: ReportingPack[];
  defaultReportingPack: ReportingPack | null;
}

export const REGULATOR_AUDIENCE = "regulators";

const REGULATOR_STATUSES: readonly RegulatorStatus[] = ["pending", "approved"];

const readStatus = (value: unknown): RegulatorStatus | null =>
  REGULATOR_STATUSES.includes(value as RegulatorStatus)
    ? (value as RegulatorStatus)
    : null;

const readReportingPacks = (value: unknown): ReportingPack[] =>
  Array.isArray(value)
    ? value.filter((pack): pack is ReportingPack =>
        REPORTING_PACKS.includes(pack as ReportingPack),
      )
    : [];

const readReportingPack = (value: unknown): ReportingPack | null =>
  REPORTING_PACKS.includes(value as ReportingPack)
    ? (value as ReportingPack)
    : null;

export const parseRegulatorMe = (
  payload: RegulatorMeResponseData | null | undefined,
): RegulatorMe => {
  const isRegulator = payload?.audience === REGULATOR_AUDIENCE;
  return {
    isRegulator,
    status: isRegulator ? readStatus(payload?.status) : null,
    portalEnabled:
      isRegulator &&
      Boolean(payload?.regulator_portal_enabled ?? payload?.portal_enabled),
    email: payload?.email ?? null,
    allowedReportingPacks: readReportingPacks(payload?.allowed_reporting_packs),
    defaultReportingPack: readReportingPack(payload?.default_reporting_pack),
  };
};

/**
 * Ask the authenticated `/me` who the caller is. Pass `accessToken` during the
 * login handshake, before the session has been persisted; omit it afterwards
 * and the stored regulator token is used.
 */
export const fetchRegulatorMe = async (
  accessToken?: string,
): Promise<RegulatorMe> => {
  const instance = createRegulatorPortalInstance(accessToken);
  const response = await instance.get<
    ApiSuccessEnvelope<RegulatorMeResponseData>
  >(API_ROUTES.REGULATOR_ME);
  return parseRegulatorMe(response.data?.data);
};
